import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
/* ================= TECH OPTIONS ================= */

const PLATFORM_OPTIONS = [
  { label: "Web Application", value: "WEB" },
  { label: "Mobile Application", value: "MOBILE" },
];

const FRONTEND_OPTIONS = {
  WEB: ["React", "Next.js"],
  MOBILE: ["React Native", "Flutter"],
};

const BACKEND_OPTIONS = [
  "Firebase",
  "Node + Express",
  "Node + Express + MongoDB",
];

/* ================= MAIN COMPONENT ================= */

export default function ProductGeneratorPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [form, setForm] = useState({
    productType: "",
    domain: "",
    platform: "",
    frontend: "",
    backend: "",
    sprintCount: 5,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sprints, setSprints] = useState(null);
  const productSeed = location.state?.productSeed;

  const frontendOptions = form.platform ? FRONTEND_OPTIONS[form.platform] : [];

  useEffect(() => {
    if (!productSeed) return;

    setForm((prev) => ({
      ...prev,
      productType: productSeed.productType || prev.productType,
      domain: productSeed.domain || prev.domain,
      platform: productSeed.platform || prev.platform,
      frontend: productSeed.frontend || prev.frontend,
      backend: productSeed.backend || prev.backend,
      sprintCount: productSeed.sprintCount || prev.sprintCount,
    }));
  }, [productSeed]);

  /* ================= FIREBASE FUNCTION ================= */

  const functions = getFunctions();
  const generateProductSprints = httpsCallable(
    functions,
    "generateProductSprints"
  );

  /* ================= GENERATE ================= */

  const generateWithAI = async () => {
    setLoading(true);
    setError("");
    setSprints(null);

    try {
      const res = await generateProductSprints({
        productType: form.productType,
        domain: form.domain,
        platform: form.platform,
        frontend: form.frontend,
        backend: form.backend,
        sprintCount: form.sprintCount,
      });

      if (!res?.data?.success) {
        throw new Error("Sprint generation failed");
      }

      setSprints(res.data.sprints);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to generate project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  const addToSprintBoard = async () => {
    try {
      if (!sprints || !sprints.length) {
        alert("No sprints to save");
        return;
      }

      if (!user) {
        alert("Please login to save project");
        return;
      }

      const sprintRef = doc(db, "sprints", user.uid); // 🔥 ONE project per user
      console.log({
        meta: {
          productType: form.productType,
          domain: form.domain,
          platform: form.platform,
          frontend: form.frontend,
          backend: form.backend,
          sprintCount: form.sprintCount,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
        },
        sprints,
        updatedAt: serverTimestamp(),
      });
      await setDoc(
        sprintRef,
        {
          meta: {
            productType: form.productType,
            domain: form.domain,
            platform: form.platform,
            frontend: form.frontend,
            backend: form.backend,
            sprintCount: form.sprintCount,
            createdBy: user.uid,
            createdAt: serverTimestamp(),
          },
          sprints,
          updatedAt: serverTimestamp(),
        },
        { merge: false } // 🔥 overwrite previous project completely
      );

      alert("✅ Project added to Sprint Board");
    } catch (err) {
      console.error("Save sprint error:", err);
      alert("❌ Failed to save project");
    }
  };

  return (
    <div style={page}>
      <h1 style={title}>🚀 Product Sprint Generator</h1>
      <p style={subtitle}>Generate industry-grade sprints & tasks using AI</p>

      {/* FORM CARD */}
      <div style={card}>
        <h3 style={sectionTitle}>Product Configuration</h3>

        <div style={grid}>
          <Field label="Product Type">
            <input
              style={input}
              placeholder="Service Booking Platform"
              onChange={(e) =>
                setForm({ ...form, productType: e.target.value })
              }
            />
          </Field>

          <Field label="Domain">
            <input
              style={input}
              placeholder="Home services / Doctor appointment"
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
            />
          </Field>

          <Field label="Platform">
            <select
              style={input}
              onChange={(e) =>
                setForm({
                  ...form,
                  platform: e.target.value,
                  frontend: "",
                })
              }
            >
              <option value="">Select</option>
              {PLATFORM_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Frontend Technology">
            <select
              style={input}
              value={form.frontend}
              onChange={(e) => setForm({ ...form, frontend: e.target.value })}
            >
              <option value="">Select</option>
              {frontendOptions.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Backend Technology">
            <select
              style={input}
              onChange={(e) => setForm({ ...form, backend: e.target.value })}
            >
              <option value="">Select</option>
              {BACKEND_OPTIONS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Number of Sprints">
            <select
              style={input}
              onChange={(e) =>
                setForm({
                  ...form,
                  sprintCount: Number(e.target.value),
                })
              }
            >
              <option value={5}>5 Sprints</option>
              <option value={6}>6 Sprints</option>
              <option value={7}>7 Sprints</option>
              <option value={8}>8 Sprints</option>
            </select>
          </Field>
        </div>

        <button
          onClick={generateWithAI}
          disabled={loading}
          style={{
            ...button,
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Generating…" : "Generate Sprints"}
        </button>

        {error && <p style={errorText}>❌ {error}</p>}
      </div>

      {/* PREVIEW */}
      {sprints && (
        <div style={{ marginTop: 48 }}>
          <h3 style={previewTitle}>📦 Generated Product Sprints</h3>

          <button style={addToBoardBtn} onClick={addToSprintBoard}>
            ➕ Add Project to Sprint Board
          </button>

          <div style={sprintGrid}>
            {sprints.map((s) => (
              <div key={s.sprintId} style={sprintCard}>
                {/* HEADER */}
                <div style={sprintHeader}>
                  <div>
                    <h4 style={sprintName}>{s.name}</h4>
                    <p style={sprintGoal}>{s.goal}</p>
                  </div>

                  <span style={sprintBadge}>Sprint {s.order}</span>
                </div>

                {/* TASKS */}
                <div style={taskList}>
                  {s.tasks.map((t) => (
                    <div key={t.id} style={taskItem}>
                      <span style={taskType(t.type)}>{t.type}</span>
                      <span style={taskTitle}>{t.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= SMALL UI HELPERS ================= */

const Field = ({ label, children }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

/* ================= STYLES ================= */

const page = {
  padding: 32,
  maxWidth: 1100,
  margin: "0 auto",
};

const title = { fontSize: 28, fontWeight: 800 };
const subtitle = { color: "#6b7280", marginBottom: 24 };

const card = {
  //background: "#ffffff",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
};

const sectionTitle = {
  fontSize: 18,
  fontWeight: 700,
  marginBottom: 16,
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 6,
};

const input = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
};

const button = {
  marginTop: 24,
  padding: "14px 20px",
  borderRadius: 12,
  border: "none",
  background: "#4f46e5",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};

const errorText = {
  marginTop: 12,
  color: "#dc2626",
  fontWeight: 600,
};

const previewCard = {
  marginTop: 20,
  padding: 20,
  borderRadius: 14,
  border: "1px solid #e5e7eb",
  // background: "#f9fafb",
};

const previewTitle = {
  fontSize: 22,
  fontWeight: 800,
  marginBottom: 24,
};

const sprintGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: 20,
};

const sprintCard = {
  // background: "#ffffff",
  borderRadius: 18,
  padding: 20,
  border: "1px solid #e5e7eb",
  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
};

const sprintHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 16,
};
const addToBoardBtn = {
  marginBottom: 24,
  padding: "14px 22px",
  borderRadius: 14,
  border: "none",
  cursor: "pointer",

  background: "linear-gradient(135deg,#22c55e,#16a34a)",
  color: "#fff",

  fontSize: 15,
  fontWeight: 800,
  letterSpacing: 0.2,

  display: "inline-flex",
  alignItems: "center",
  gap: 8,

  boxShadow: "0 12px 30px rgba(34,197,94,0.35)",
};

const sprintName = {
  fontSize: 16,
  fontWeight: 800,
  marginBottom: 4,
};

const sprintGoal = {
  fontSize: 13,
  color: "#6b7280",
  lineHeight: 1.5,
};

const sprintBadge = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",

  height: 28, // 👈 key
  padding: "0 14px", // 👈 horizontal padding

  background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
  color: "#fff",

  borderRadius: 9999, // 👈 true pill
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const taskList = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const taskItem = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  borderRadius: 12,
  // background: "#f9fafb",
  border: "1px solid #e5e7eb",
};

const taskTitle = {
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.4,
};

/* TYPE BADGES */
const taskType = (type) => ({
  fontSize: 11,
  fontWeight: 800,
  padding: "4px 8px",
  borderRadius: 999,
  color: "#fff",
  background:
    type === "UI"
      ? "#6366f1"
      : type === "DATABASE"
      ? "#0ea5e9"
      : type === "SECURITY"
      ? "#ef4444"
      : type === "DEPLOY"
      ? "#22c55e"
      : "#64748b",
  whiteSpace: "nowrap",
});
